import type { ImageLoaderProps } from 'next/image';

/**
 * Widths the image service is allowed to produce.
 *
 * MUST stay in sync with the width allowlist in the CloudFront viewer-request
 * function. A width missing there is rejected at the edge; a width missing here
 * is never requested. Both directions are silent, so change them together.
 */
export const TRANSFORM_WIDTHS = [64, 128, 256, 384, 640, 828, 1200, 1920] as const;

/** Hosts whose objects the image service can transform. */
const TRANSFORMABLE_HOST_SUFFIXES = ['.tixticketing.com', '.tixly.com'];

/**
 * The image service resizes on the fly but only for an allowlisted set of
 * widths, so snap the width Next asks for up to the next one it can serve.
 * Snapping up rather than down keeps the image from being upscaled by the
 * browser.
 */
const largestTransformWidth = Math.max(...TRANSFORM_WIDTHS);
export const snapToAllowedWidth = (width: number): number => TRANSFORM_WIDTHS.find(allowed => allowed >= width) ?? largestTransformWidth;

/**
 * Whether a URL points at something the image service can transform.
 *
 * Excludes SVGs (vector already, and rasterising them loses the point),
 * inline data/blob URLs, and relative paths, which are app-local assets rather
 * than CDN objects.
 */
export const isTransformableUrl = (src: string): boolean => {
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
  if (url.pathname.toLowerCase().endsWith('.svg')) return false;
  return TRANSFORMABLE_HOST_SUFFIXES.some(suffix => url.hostname.endsWith(suffix));
};

/**
 * Builds the `next/image` loader for a given source URL, or `undefined` to let
 * `next/image` fall back to its default behaviour.
 *
 * Returning `undefined` is the inert state: the CDN in front of the image
 * objects serves them verbatim and ignores transform parameters, so until the
 * image service is deployed there is nothing to ask it for. Set
 * `NEXT_PUBLIC_IMAGE_CDN_TRANSFORMS=true` per environment once it is live.
 */
export const getImageLoader = (src: string): ((props: ImageLoaderProps) => string) | undefined => {
  if (process.env.NEXT_PUBLIC_IMAGE_CDN_TRANSFORMS !== 'true') return undefined;
  if (!isTransformableUrl(src)) return undefined;
  return ({
    width
  }: ImageLoaderProps) => {
    const url = new URL(src);
    url.searchParams.set('width', String(snapToAllowedWidth(width)));
    url.searchParams.set('format', 'webp');
    return url.href;
  };
};